import React, { useState, useRef, useMemo, useEffect } from "react";
import { Box, Typography, ClickAwayListener, Popper } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { DateRange } from "react-date-range";

const DateRangeSlider = ({ onDateRangeSelected, dataSource }) => {
  const today = new Date();
  const oneMonthAgo = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate()
  );
  const [state, setState] = useState([
    {
      startDate: oneMonthAgo,
      endDate: null,
      key: "selection",
    },
  ]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {

    const storedDateRange = sessionStorage.getItem("selectedDateRange");
    if (storedDateRange) {
      const parsedDateRange = JSON.parse(storedDateRange);
      setState([
        {
          ...parsedDateRange,
          startDate: new Date(parsedDateRange.startDate),
          endDate: parsedDateRange.endDate
            ? new Date(parsedDateRange.endDate)
            : null,
          key: "selection",
        },
      ]);
      onDateRangeSelected(parsedDateRange);
    }
  }, []);

  const highlightedDates = useMemo(() => {
    return dataSource.reduce((acc, item) => {
      const date = new Date(item.properties.acquisition_info.date);
      acc[date.toDateString()] = { color: "rgba(34, 197, 94, 0.8)" };
      return acc;
    }, {});
  }, [dataSource]);

  const dayContentRenderer = (date) => {
    const key = date.toDateString();
    if (highlightedDates[key]) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "90%",
            height: "100%",
            backgroundColor: highlightedDates[key].color,
            borderRadius: "40%",
            color: "white",
          }}
        >
          <span>{date.getDate()}</span>
        </div>
      );
    }
    return null;
  };

  const handleSelect = (ranges) => {
    setState([ranges.selection]);
    sessionStorage.setItem(
      "selectedDateRange",
      JSON.stringify(ranges.selection)
    );
    onDateRangeSelected(ranges.selection);
  };

  const handleCalendarClick = (event) => {
    setAnchorEl(event.currentTarget);
    setShowCalendar((prev) => !prev);
  };

  const handleClickAway = (event) => {
    if (anchorEl && anchorEl.contains(event.target)) {
      return;
    }
    setShowCalendar(false);
  };

  const formatDate = (date) => {
    return date
      ? `${date.getDate()} ${date.toLocaleString("default", {
          month: "short",
        })} ${date.getFullYear()}`
      : "";
  };

  const isDateRangeActive = state[0].startDate && state[0].endDate;

  return (
    <Box >
      <Typography variant="h7">Date</Typography>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box>
          <Box
            className="date-range-slider"
            onClick={handleCalendarClick}
            sx={{
              cursor: "pointer",
              border: "2px solid",
              borderColor: isDateRangeActive
                ? "primary.main"
                : "rgba(0, 0, 0, 0.2)",
              transition: "all 0.3s ease",
              backgroundColor: isDateRangeActive
                ? "rgba(0, 0, 0, 0.04)"
                : "transparent",
              display: "flex",
              alignItems: "center",
            }}
          >
            <CalendarTodayIcon
              sx={{
                mr: 1,
                color: isDateRangeActive ? "primary.main" : "inherit",
              }}
            />
            <Typography
              sx={{
                color: isDateRangeActive ? "primary.main" : "inherit",
                fontWeight: isDateRangeActive ? "bold" : "normal",
              }}
            >
              {formatDate(state[0].startDate)} - {formatDate(state[0].endDate)}
            </Typography>
          </Box>
          <Popper
            open={showCalendar}
            anchorEl={anchorEl}
            style={{ zIndex: 2000 }} 
          >
            <Box> 
              <DateRange
                editableDateInputs={true}
                onChange={handleSelect}
                moveRangeOnFirstSelection={false}
                ranges={state}
                dayContentRenderer={dayContentRenderer}
              />
            </Box>
          </Popper>
        </Box>
      </ClickAwayListener>
    </Box>
  );
};

export default DateRangeSlider;
